import { ContentCopy, Edit, Email } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import InfoBlock from '~/components/infoBlock'
import { TInfoButton } from '~/components/infoBlock/infoButton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { EEmailStatus } from '~/features/settings/models'
import { EClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'

type TAgentEmailInfoProps = {
  isExpanded: boolean
  tier: ECognitoGroups
}

function AgentEmailInfo({ isExpanded, tier }: TAgentEmailInfoProps) {
  const navigate = useNavigate()
  const isPartner = useIsPartner()
  const { selectedAgent: agent } = useGetAgentCentralized()

  const agentEmail = agent?.[EAgentKeys.agentEmail]
  const agentEmailStatus = agent?.[EAgentKeys.emailStatus]

  const isChannelActive = agentEmailStatus === EEmailStatus.active
  const isFreePlan = tier === ECognitoGroups.customerFreePlan

  let message = agentEmail
  let showActiveActions = true

  if (!isFreePlan) {
    if (!agentEmail) {
      message = isChannelActive
        ? 'Set up your email address.'
        : 'Activate email'
      showActiveActions = false
    }

    if (agentEmail && !isChannelActive) {
      message = 'Activate email'
      showActiveActions = false
    }
  }

  if (isFreePlan) {
    message = 'Upgrade to enable email.'
    showActiveActions = false
  }

  const handleEditEmail = () => {
    if (!showActiveActions && isFreePlan) {
      navigate(isPartner ? EClientPath.account : EClientPath.billing)
      return
    }

    navigate(EClientPath.settingsEmail)
  }

  const handleSendEmail = () => {
    window.location.href = `mailto:${agentEmail}`
  }

  const activeActions: TInfoButton[] = [
    {
      icon: <ContentCopy />,
      label: 'Copy',
      variant: 'outlined',
      textToCopy: agentEmail,
    },
    {
      icon: <Edit />,
      label: 'Edit',
      variant: 'outlined',
      onClick: handleEditEmail,
    },
    {
      label: 'Email',
      variant: 'contained',
      onClick: handleSendEmail,
    },
  ]

  const disabledActions: TInfoButton[] = [
    {
      icon: <Edit />,
      label: 'Edit',
      variant: 'outlined',
      onClick: handleEditEmail,
    },
  ]

  const actions = showActiveActions ? activeActions : disabledActions

  return (
    <InfoBlock
      icon={<Email />}
      infoText={
        <>
          <p>How to use your AI assistant email address</p>
          <ul style={{ margin: 0, paddingLeft: 16, paddingBottom: 16 }}>
            <li>
              First customize your email settings, customizing the signature,
              editing the email address, and more
            </li>
            <li>
              Tier-permitting, set up a custom email address (e.g.,
              hello@yourdomain.com) that forwards to your assistant
            </li>
            <li>
              Update your website and other marketing collatoral with your
              assistant email address
            </li>
            <li>
              Add your assistant email as the reply-to address in marketing
              campaigns
            </li>
          </ul>
        </>
      }
      title="Email"
      value={message}
      isExpanded={isExpanded}
      actionButtons={actions}
      onLabelClick={showActiveActions ? handleSendEmail : handleEditEmail}
    />
  )
}

export default AgentEmailInfo
